import React from 'react';
import {
  UserCheck, CalendarCheck, RefreshCw, Globe, MessageCircle, ShieldCheck,
  TrendingUp, ArrowLeftRight, Lock, FileText, Bell, Headset, DollarSign,
  Package, Clock, Scale, AlertTriangle,
} from 'lucide-react';
import type { JourneyResource } from '../../../../types/journey';
import { useJourneyGlossary } from './JourneyGlossaryContext';
import { useGoToLiquidacion } from './JourneyNavigationContext';

// Contenido de "Tu camino inicial" — copy operativo real de Psi Mammoliti, adaptado del mockup
// (tu-camino-inicial-mockup-v6.html). Hardcodeado en el frontend a propósito: no hay panel admin
// de contenido (spec Parte 4.2) ni tabla `induction_content` — la lógica de qué mostrar según
// etapa es la que definimos, la data para decidir la etapa ya está disponible en Psi Platform.

const HIGHLIGHT_STYLES = {
  gray: { box: 'bg-gray-50 border-gray-200', title: 'text-gray-900', body: 'text-gray-700' },
  green: { box: 'bg-green-50 border-green-200', title: 'text-green-900', body: 'text-green-700' },
  blue: { box: 'bg-blue-50 border-blue-200', title: 'text-blue-900', body: 'text-blue-700' },
  violet: { box: 'bg-violet-50 border-violet-200', title: 'text-violet-900', body: 'text-violet-700' },
  amber: { box: 'bg-amber-50 border-amber-200', title: 'text-amber-900', body: 'text-amber-700' },
} as const;

const Highlight: React.FC<{ title: string; children: React.ReactNode; color?: keyof typeof HIGHLIGHT_STYLES }> = ({
  title, children, color = 'gray',
}) => {
  const s = HIGHLIGHT_STYLES[color];
  return (
    <div className={`border rounded-lg px-3.5 py-3 my-2.5 ${s.box}`}>
      <strong className={`block mb-1 ${s.title}`}>{title}</strong>
      <div className={s.body}>{children}</div>
    </div>
  );
};

const WarningBox: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 my-2.5 flex gap-2.5 items-start">
    <AlertTriangle size={16} className="text-red-700 shrink-0 mt-0.5" />
    <div>
      <strong className="block text-red-800 mb-1">{title}</strong>
      <div className="text-red-700">{children}</div>
    </div>
  </div>
);

// Recuadro de nota/referencia chica (definiciones, aclaraciones) — celeste, distinto de Highlight.
const InfoBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="border border-blue-100 rounded-lg bg-blue-50/60 px-3.5 py-3 mt-3 text-xs text-blue-900 leading-relaxed space-y-1.5">
    {children}
  </div>
);

const BADGE_STYLES = {
  amber: 'bg-amber-100 text-amber-800',
  green: 'bg-green-100 text-green-800',
  blue: 'bg-blue-100 text-blue-800',
  violet: 'bg-violet-100 text-violet-800',
  red: 'bg-red-100 text-red-800',
} as const;

// Sin recuadro a propósito — solo negrita, para no competir visualmente con el resto del texto.
const Badge: React.FC<{ children: React.ReactNode; color?: keyof typeof BADGE_STYLES }> = ({ children }) => (
  <strong className="font-semibold whitespace-nowrap">{children}</strong>
);

const HourBadge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Badge color="amber">{children}</Badge>
);

// Término clickeable — abre el panel de detalle (GlossaryTermPanel) con el paso a paso real de
// cómo hacer esa acción en la app. `term` debe existir como key en JOURNEY_GLOSSARY.
export const ClickableTerm: React.FC<{ term: string; children: React.ReactNode }> = ({ term, children }) => {
  const openTerm = useJourneyGlossary();
  return (
    <button
      type="button"
      onClick={() => openTerm(term)}
      className="font-medium text-violet-700 underline decoration-dotted decoration-violet-300 underline-offset-2 hover:text-violet-900 hover:decoration-violet-500"
    >
      {children}
    </button>
  );
};

// Enlace que cambia a la sub-tab "Liquidación" (la otra sub-tab visible durante el journey NZ).
const LiquidacionLink: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const goToLiquidacion = useGoToLiquidacion();
  return (
    <button
      type="button"
      onClick={goToLiquidacion}
      className="font-medium text-violet-700 underline decoration-dotted decoration-violet-300 underline-offset-2 hover:text-violet-900 hover:decoration-violet-500"
    >
      {children}
    </button>
  );
};

export const JOURNEY_RESOURCES: JourneyResource[] = [
  // ── Etapa 0 ──────────────────────────────────────────────────────────────
  {
    resource_key: 'primera-sesion-intocable',
    stage: 0,
    position: 1,
    title: '🔒 La primera sesión es casi intocable',
    subtitle: 'No se reprograma como el resto — define si el proceso arranca',
    icon: Lock,
    iconBgClassName: 'bg-red-100',
    iconColorClassName: 'text-red-700',
    isNewInStage: false,
    checkbox_label: 'Entendí que la primera sesión de un paciente no se reprograma como el resto.',
    body: (
      <>
        <p className="!mb-2">
          La primera sesión de cualquier paciente no se reprograma como el resto — es el momento
          que define si el proceso arranca o no.
        </p>
        <Highlight title="⚠️ Muy excepcionalmente se puede reprogramar" color="amber">
          Solo ante una situación genuinamente excepcional — nunca por preferencia o comodidad de
          agenda.
        </Highlight>
        <p className="text-xs text-gray-400 !mt-1">
          Por eso es fundamental tener tu agenda 100% actualizada todo el tiempo.
        </p>
      </>
    ),
  },
  // ── Etapa 1 ──────────────────────────────────────────────────────────────
  {
    resource_key: 'agendar-formulario',
    stage: 1,
    position: 1,
    title: '📅 Agendar o completar el formulario — siempre',
    subtitle: 'Las dos únicas opciones al terminar cada sesión',
    icon: CalendarCheck,
    iconBgClassName: 'bg-blue-100',
    iconColorClassName: 'text-blue-700',
    isNewInStage: true,
    checkbox_label: 'Entendí que al terminar cada sesión debo agendar o completar el formulario — sin excepción.',
    body: (
      <>
        <p className="!mb-2">Al terminar cada sesión, tenés exactamente dos opciones. No existe una tercera.</p>
        <ul className="list-disc ml-4 mb-2 space-y-1">
          <li>📆 <ClickableTerm term="agendar-proxima-sesion">Agendar la próxima sesión</ClickableTerm> — desde la lista de Pacientes. Siempre confirmá antes de agendar.</li>
          <li>📝 <ClickableTerm term="formulario-no-agendamiento">Completar el formulario de no agendamiento</ClickableTerm> — el motivo define el seguimiento automático al paciente.</li>
        </ul>
        <p>Si no hacés ninguna de las dos, a las <Badge color="red">24hs</Badge> el sistema te avisa por WhatsApp. Llegar a ese punto ya es una falta.</p>
      </>
    ),
  },
  {
    resource_key: 'reprogramaciones',
    stage: 1,
    position: 2,
    title: '🔄 Reprogramaciones y cancelaciones',
    subtitle: '6hs si está paga, 12hs si no — nunca sin confirmación',
    icon: RefreshCw,
    iconBgClassName: 'bg-amber-100',
    iconColorClassName: 'text-amber-700',
    isNewInStage: true,
    checkbox_label: 'Entendí la política de reprogramaciones, las dos vías de gestión y que nunca debo cancelar sin confirmación.',
    body: (
      <>
        <p className="!mb-1"><strong className="font-medium text-gray-900">¿Cuándo se puede reprogramar o cancelar sin costo?</strong></p>
        <ul className="list-disc ml-4 mb-3 space-y-1.5">
          <li><strong className="font-medium text-gray-900">Sesión ya paga:</strong> sin costo hasta <HourBadge>6hs antes</HourBadge>.</li>
          <li><strong className="font-medium text-gray-900">Sesión sin pagar:</strong> el sistema la cancela solo a las <HourBadge>12hs antes</HourBadge> por falta de pago — no tenés que intervenir vos.</li>
        </ul>
        <p className="!mb-1"><strong className="font-medium text-gray-900">Dos vías para gestionarlo:</strong></p>
        <ul className="list-disc ml-4 mb-2 space-y-1.5">
          <li>💻 Desde su propia plataforma de pacientes (el equivalente a la tuya).</li>
          <li>💬 Escribiendo directamente por WhatsApp, donde nuestro equipo lo asiste.</li>
        </ul>
        <Highlight title="🚫 Nunca canceles ni reprogames una sesión por tu cuenta" color="amber">
          <p className="!mb-2">
            Necesitás sí o sí (a) la confirmación del paciente y/o (b) el consentimiento de nuestro
            equipo administrativo antes de mover o cancelar cualquier sesión.
          </p>
          <p>
            Avisale al paciente <strong className="font-medium">antes</strong> de
            reprogramar — nunca tiene que enterarse por un mail automático de cancelación que le
            llega sin previo aviso. Si no podés comunicarte con él o no te responde por mail,
            contactá a CX: ellos pueden avisarle directamente.
          </p>
        </Highlight>
        <p className="text-xs text-gray-400 !mt-1">
          Dos reprogramaciones seguidas son además una señal clínica — abordalo en sesión.
        </p>
      </>
    ),
  },
  {
    resource_key: 'primera-sesion-intocable',
    stage: 1,
    position: 3,
    title: '🔒 La primera sesión es casi intocable',
    subtitle: 'No se reprograma como el resto — define si el proceso arranca',
    icon: Lock,
    iconBgClassName: 'bg-red-100',
    iconColorClassName: 'text-red-700',
    isNewInStage: true,
    checkbox_label: 'Entendí que la primera sesión de un paciente no se reprograma como el resto.',
    body: (
      <>
        <p className="!mb-2">
          La primera sesión de cualquier paciente no se reprograma como el resto — es el momento
          que define si el proceso arranca o no.
        </p>
        <Highlight title="⚠️ Muy excepcionalmente se puede reprogramar" color="amber">
          Solo ante una situación genuinamente excepcional — nunca por preferencia o comodidad de
          agenda.
        </Highlight>
        <p className="text-xs text-gray-400 !mt-1">
          Por eso es fundamental tener tu agenda 100% actualizada todo el tiempo.
        </p>
      </>
    ),
  },
  {
    resource_key: 'sesion-no-show',
    stage: 1,
    position: 4,
    title: '⏰ Si el paciente no aparece y no avisó',
    subtitle: 'Esperar 30 min, avisar a CX, la sesión se paga igual',
    icon: Clock,
    iconBgClassName: 'bg-pink-100',
    iconColorClassName: 'text-pink-700',
    isNewInStage: true,
    checkbox_label: 'Entendí qué hacer si el paciente no aparece sin avisar y que la sesión se paga igual.',
    body: (
      <>
        <ol className="list-decimal ml-4 mb-2 space-y-1.5">
          <li>📞 <strong className="font-medium text-gray-900">Avisar a CX de inmediato</strong> — para que el equipo contacte al paciente y entienda qué pasó.</li>
          <li>⏳ <strong className="font-medium text-gray-900">Esperar al menos <Badge color="amber">30 minutos</Badge></strong> — desde el horario agendado, dentro de la sesión.</li>
        </ol>
        <Highlight title="💰 La sesión se paga igual" color="green">
          Si esperaron los 30 minutos y avisaron a CX, la sesión se abona con normalidad — el
          paciente es quien no se presentó.
        </Highlight>
      </>
    ),
  },
  {
    resource_key: 'zona-horaria',
    stage: 1,
    position: 5,
    title: '🌎 Zona horaria',
    subtitle: 'Agenda en tu zona configurada, hablá en la del paciente',
    icon: Globe,
    iconBgClassName: 'bg-teal-100',
    iconColorClassName: 'text-teal-700',
    isNewInStage: true,
    checkbox_label: 'Entendí cómo funciona la zona horaria y que debo coordinar con el paciente en su hora local.',
    body: (
      <>
        <p>
          En el sistema agendás en la zona horaria que vos tenés configurada en tu perfil — no es necesariamente hora
          Argentina: si configuraste Colombia, por ejemplo, tu agenda va a estar en hora de Colombia. Con el paciente,
          siempre coordinás en su hora local. El sistema ajusta los recordatorios automáticamente al GMT de cada uno.
          Si tu GMT o el del paciente están mal configurados, corregilo antes de agendar.
        </p>
        <InfoBox>
          <p>
            <strong className="font-medium underline">🌐 Zona/huso horario:</strong> la región donde vive
            alguien (ej: hora de Argentina, hora de Colombia).
          </p>
          <p>
            <strong className="font-medium underline">🕒 GMT:</strong> el desfasaje puntual respecto al
            meridiano de Greenwich que le corresponde a esa zona (ej: Argentina es GMT-3, Colombia es GMT-5) — es el
            valor que usa el sistema para calcular y mostrar cada horario correctamente.
          </p>
        </InfoBox>
      </>
    ),
  },
  {
    resource_key: 'primera-sesion',
    stage: 1,
    position: 6,
    title: '💬 Primera sesión y encuadre',
    subtitle: 'Estructura, frecuencia, duración, puntualidad y cobro',
    icon: MessageCircle,
    iconBgClassName: 'bg-violet-100',
    iconColorClassName: 'text-violet-700',
    isNewInStage: true,
    checkbox_label: 'Entendí la estructura de la primera sesión y lo que debo reforzar con el paciente.',
    body: (
      <>
        <p className="!mb-2">La primera sesión no es para resolver — es para evaluar y establecer el vínculo.</p>
        <ul className="list-disc ml-4 mb-2 space-y-1">
          <li>🤝 Presentación y encuadre del espacio</li>
          <li>🔍 Exploración del motivo de consulta</li>
          <li>📋 Contrato terapéutico: duración, frecuencia, política de cancelación</li>
          <li>✅ Cierre: espacio para dudas + agendar la próxima sesión</li>
        </ul>
        <p className="!mb-2">Sesiones de <Badge color="violet">50-60 minutos</Badge>. Si el paciente no se conecta a los 5 min, avisá a CX por WhatsApp.</p>
        <Highlight title="✨ Dos cosas a reforzar con el paciente en la primera sesión" color="violet">
          <ol className="list-decimal ml-4 space-y-1">
            <li>Avisar con al menos <HourBadge>6hs</HourBadge> si no puede asistir — si no, pierde la sesión sin reembolso.</li>
            <li>Frecuencia semanal recomendada — explicale por qué importa.</li>
          </ol>
        </Highlight>
        <Highlight title="😌 El cobro no es tu problema" color="green">
          Si el paciente no abona antes de las 6hs previas, la sesión se cancela sola. No tenés que intervenir.
        </Highlight>
      </>
    ),
  },
  {
    resource_key: 'comunicacion-pacientes',
    stage: 1,
    position: 7,
    title: '📨 Medios de comunicación entre el paciente y vos',
    subtitle: 'Solo por canales Psi — falta grave si no',
    icon: ShieldCheck,
    iconBgClassName: 'bg-gray-200',
    iconColorClassName: 'text-gray-700',
    isNewInStage: true,
    checkbox_label: 'Entendí que solo puedo comunicarme con pacientes por Meet y mi mail @psimammoliti.com, y que cualquier excepción requiere aprobación previa.',
    body: (
      <>
        <p className="!mb-1"><strong className="font-medium text-gray-900">Cómo comunicarte con el paciente:</strong></p>
        <ul className="list-disc ml-4 mb-3 space-y-1.5">
          <li>🎥 <strong className="font-medium text-gray-900">Sincrónico:</strong> videollamada por Google Meet, solo durante la sesión.</li>
          <li>
            ✉️ <strong className="font-medium text-gray-900">Asincrónico:</strong> exclusivamente tu mail profesional{' '}
            <strong className="font-medium text-gray-900">@psimammoliti.com</strong> — ningún otro mail, ni personal
            ni de otra cuenta.
          </li>
        </ul>
        <WarningBox title="🚫 Prohibido — falta grave">
          WhatsApp personal, Zoom, Instagram u otros canales. Usarlos es motivo de expulsión del equipo.
        </WarningBox>
        <p className="!mt-1">Antes de la primera sesión, cualquier consulta del paciente va a CX — no la manejés vos.</p>
        <InfoBox>
          Si puntualmente necesitás otro medio como excepción, se puede evaluar — pero solo con pedido explícito tuyo
          y aceptación previa de nuestro equipo. Nunca por tu cuenta.
        </InfoBox>
      </>
    ),
  },
  {
    resource_key: 'comunicaciones-automaticas',
    stage: 1,
    position: 8,
    title: '🔔 Qué recibe el paciente automáticamente',
    subtitle: 'Confirmaciones, recordatorios, seguimiento sin sesión',
    icon: Bell,
    iconBgClassName: 'bg-green-100',
    iconColorClassName: 'text-green-700',
    isNewInStage: true,
    checkbox_label: 'Entendí qué comunicaciones recibe el paciente automáticamente, incluido el seguimiento cuando no tiene sesión agendada.',
    body: (
      <>
        <p className="!mb-1"><strong className="font-medium text-gray-900">📌 Con sesión agendada:</strong></p>
        <ul className="list-disc ml-4 mb-3 space-y-1.5">
          <li>✅ Confirmación con el link de Meet, fecha y hora en su zona horaria.</li>
          <li>⏰ Recordatorios automáticos antes de la sesión.</li>
          <li>🔁 Notificaciones ante cancelaciones o reprogramaciones.</li>
        </ul>
        <p className="!mb-1"><strong className="font-medium text-gray-900">📭 Sin sesión agendada:</strong></p>
        <ul className="list-disc ml-4 mb-2 space-y-1.5">
          <li>El sistema le manda mensajes de seguimiento automáticos para retomar el contacto.</li>
          <li>
            Esto depende de vos: completá el <strong className="font-medium text-gray-900">formulario de no
            agendamiento</strong> al terminar cada sesión sin agendar la próxima, así el sistema sabe en qué quedó el
            proceso y arma el seguimiento correcto.
          </li>
        </ul>
        <Highlight title="😌 No tenés que gestionar nada de esto manualmente" color="green">
          El sistema se encarga de todo el seguimiento automático.
        </Highlight>
      </>
    ),
  },

  // ── Etapa 2 ──────────────────────────────────────────────────────────────
  {
    resource_key: 'proceso-activo',
    stage: 2,
    position: 1,
    title: '🤝 ¿Qué es un proceso activo?',
    subtitle: 'La base para entender cómo avanzás en Psi Mammoliti',
    icon: UserCheck,
    iconBgClassName: 'bg-violet-100',
    iconColorClassName: 'text-violet-700',
    isNewInStage: true,
    checkbox_label: 'Entendí qué es un proceso activo y cuándo se activa.',
    body: (
      <>
        <p className="!mb-2">
          En Psi Mammoliti, un <strong className="font-medium text-gray-900">proceso</strong> es la relación
          terapéutica entre vos y un paciente. No es una sesión — es el vínculo completo.
        </p>
        <Highlight title="✨ ¿Cuándo se activa?" color="blue">Cuando completás tu primera sesión con ese paciente.</Highlight>
        <p>Un proceso deja de estar activo cuando pasan <Badge color="blue">30 días</Badge> de su última sesión, sea por el motivo que fuera.</p>
      </>
    ),
  },
  {
    resource_key: 'performance',
    stage: 2,
    position: 2,
    title: '📈 Cómo se calcula tu performance',
    subtitle: 'Qué miden, qué impacta, cómo mejorar',
    icon: TrendingUp,
    iconBgClassName: 'bg-violet-100',
    iconColorClassName: 'text-violet-700',
    isNewInStage: true,
    checkbox_label: 'Entendí cómo se calcula mi performance y qué acciones impactan positivamente.',
    body: (
      <>
        <p className="!mb-1">Las métricas que miramos:</p>
        <ul className="list-disc ml-4 mb-2 space-y-1">
          <li>🔁 <strong className="font-medium text-gray-900">Tasa de segunda sesión:</strong> ¿cuántos volvieron en los primeros <Badge color="violet">30 días</Badge>?</li>
          <li>📆 <strong className="font-medium text-gray-900">Tasa de continuidad:</strong> ¿cuántos llegaron a la 5ta sesión dentro de los <Badge color="violet">45 días</Badge>?</li>
          <li>📊 <strong className="font-medium text-gray-900">Adherencia general:</strong> porcentaje de procesos activos con frecuencia regular.</li>
        </ul>
        <Highlight title="🚀 Lo que más impacta" color="green">
          Agendar al terminar cada sesión, proponer frecuencia semanal desde el inicio, abordar
          clínicamente los signos de abandono antes de que ocurran.
        </Highlight>
      </>
    ),
  },
  {
    resource_key: 'derivaciones',
    stage: 2,
    position: 3,
    title: '🔀 Derivaciones',
    subtitle: 'Cuándo aplica y cómo hacerlo correctamente',
    icon: ArrowLeftRight,
    iconBgClassName: 'bg-teal-100',
    iconColorClassName: 'text-teal-700',
    isNewInStage: true,
    checkbox_label: 'Entendí cuándo corresponde derivar y cuál es el procedimiento obligatorio.',
    body: (
      <>
        <p className="!mb-2 font-medium text-gray-900">Derivar es la excepción, no la regla:</p>
        <ul className="list-none mb-2 space-y-1.5">
          <li>❌ Solo <Badge color="red">1 de cada 5</Badge> derivaciones tiene éxito (la mayoría de los pacientes abandona el proceso cuando se los deriva).</li>
          <li>✅ El <Badge color="green">80%</Badge> de los motivos de consulta que llegan los podés trabajar vos mismo/a.</li>
        </ul>
        <div className="border border-gray-100 rounded-lg bg-gray-50 px-3.5 py-2.5 mb-3 text-xs text-gray-400 leading-relaxed">
          Evaluá en sesión antes de pensar en derivar — nunca es la primera opción.
        </div>
        <p className="!mb-1">
          <strong className="font-medium text-gray-900">✅ Cuándo corresponde:</strong>{' '}
          cuando el caso excede tu formación, hay riesgo clínico real, o requiere una especialidad muy específica.
        </p>
        <p className="!mb-2">
          <strong className="font-medium text-gray-900">🚫 Cuándo NO:</strong> si el motivo está en tus áreas registradas
          (pedir una derivación para temática que sí cubrís es una falta grave).
        </p>
        <Highlight title="📋 Procedimiento obligatorio" color="blue">
          📧 Escribinos a psicos@psimammoliti.com contando el caso — inicialmente, toda derivación que consideres necesita ser aceptada por nuestro equipo antes de avanzar. Lo analizamos y te decimos cómo continuar. Hasta ese momento, seguí atendiendo al paciente con normalidad.
        </Highlight>
      </>
    ),
  },
  {
    resource_key: 'bloqueos-agenda',
    stage: 2,
    position: 4,
    title: '🚧 Bloqueos de agenda',
    subtitle: 'Tipos, cuándo aplican, consecuencias',
    icon: Lock,
    iconBgClassName: 'bg-amber-100',
    iconColorClassName: 'text-amber-700',
    isNewInStage: true,
    checkbox_label: 'Entendí los tipos de bloqueo de agenda y cómo gestionarlos correctamente.',
    body: (
      <>
        <p className="!mb-3">Un bloqueo implica dejar de recibir nuevos pacientes, sin afectar los procesos que ya tenés.</p>
        <ul className="list-disc ml-4 mb-3 space-y-2.5">
          <li><ClickableTerm term="bloqueo-sin-horarios">⏳ Sin horarios de reprogramación</ClickableTerm>: si tenés menos de <Badge color="amber">4 horarios</Badge> libres para reprogramación en los próximos 7 días. Se desbloquea al volver a tener 4 horarios libres.</li>
          <li><ClickableTerm term="bloqueo-demasiados-pacientes">📈 Demasiados pacientes</ClickableTerm>: al alcanzar tu límite de procesos vigentes (por defecto <Badge color="amber">40</Badge>).</li>
          <li><ClickableTerm term="bloqueo-manual">✍️ Situaciones personales extraordinarias</ClickableTerm>: cualquier situación que implique que tengas que dejar de atender, sin fecha de reincorporación programada.</li>
          <li><ClickableTerm term="bloqueo-licencias">🏖️ Días Off</ClickableTerm>: cualquier situación que implique que tengas que dejar de atender por unos días específicos. Sabés exactamente la fecha de reincorporación.</li>
        </ul>
        <p className="!mt-3">El sistema solo permite agendar en los horarios que configuraste. Si querés agregar más, actualizalos en la plataforma.</p>
      </>
    ),
  },
  {
    resource_key: 'historia-clinica',
    stage: 2,
    position: 5,
    title: '📋 Historia clínica',
    subtitle: 'Registrá siempre el mismo día',
    icon: FileText,
    iconBgClassName: 'bg-pink-100',
    iconColorClassName: 'text-pink-700',
    isNewInStage: true,
    checkbox_label: 'Entendí que debo registrar la historia clínica el mismo día de cada sesión.',
    body: (
      <>
        <p className="!mb-2">Registrá la historia clínica antes de cerrar el día — no mañana.</p>
        <Highlight title="🗂️ Por qué importa" color="blue">
          El registro inmediato garantiza que si algo cambia, la continuidad del paciente no se pierde.
        </Highlight>
      </>
    ),
  },
  // Nota: 'pagos-equidad-sesion' se repite en Etapa 3 (más abajo) a pedido explícito — misma
  // resource_key a propósito para compartir el estado de aceptación entre ambas apariciones,
  // igual que 'primera-sesion-intocable' entre Etapa 0 y Etapa 1. Si editás el body de una copia,
  // replicá el cambio en la otra para que no diverjan.
  {
    resource_key: 'pagos-equidad-sesion',
    stage: 2,
    position: 6,
    title: 'Equidad en la sesión',
    subtitle: 'Qué pasa con el pago según quién no cumplió',
    icon: Scale,
    iconBgClassName: 'bg-violet-100',
    iconColorClassName: 'text-violet-700',
    isNewInStage: true,
    checkbox_label: 'Entendí cómo funciona la equidad de pago entre vos y el paciente.',
    body: (
      <>
        <p className="!mb-3">
          La equidad en la sesión significa que las consecuencias de un incumplimiento — de las
          políticas de cancelación y reprogramación — las asume quien lo generó: vos o el
          paciente, según de quién haya sido la falla.
        </p>
        <p className="!mb-1"><strong className="font-medium text-gray-900">⚖️ La equidad funciona en los dos sentidos:</strong></p>
        <ul className="list-disc ml-4 mb-3 space-y-1.5">
          <li>Si por un error o imprevisto tuyo no podés dar una sesión acordada, esa sesión se reprograma y además se agenda una sesión gratis como compensación al paciente — la asumen entre vos y CX (responsabilidad solidaria).</li>
          <li>Si el paciente no avisa con <HourBadge>6hs</HourBadge> y no se presenta, pierde la sesión sin reembolso.</li>
        </ul>
        <InfoBox>
          Si el error fue de CX u operaciones, se reprograma sin sesión gratuita adicional, pero se pide tu flexibilidad para reubicar el espacio.
        </InfoBox>
      </>
    ),
  },
  {
    resource_key: 'pagos-profesionales-redirect',
    stage: 2,
    position: 7,
    title: '💰 Pagos a las profesionales',
    subtitle: 'Dónde encontrar la información',
    icon: DollarSign,
    iconBgClassName: 'bg-green-100',
    iconColorClassName: 'text-green-700',
    isNewInStage: true,
    checkbox_label: 'Entendí dónde encontrar la información sobre pagos.',
    body: (
      <p>
        Para la información sobre los pagos, ingresá a la pestaña <LiquidacionLink>Liquidación</LiquidacionLink>.
      </p>
    ),
  },

  // ── Etapa 3 ──────────────────────────────────────────────────────────────
  {
    resource_key: 'canales-equipo',
    stage: 3,
    position: 1,
    title: '🎧 Canales del equipo',
    subtitle: 'WhatsApp para urgencias, mail para consultas',
    icon: Headset,
    iconBgClassName: 'bg-blue-100',
    iconColorClassName: 'text-blue-700',
    isNewInStage: true,
    checkbox_label: 'Entendí cuáles son los canales del equipo y para qué sirve cada uno.',
    body: (
      <ul className="list-disc ml-4 space-y-2">
        <li>
          Para urgencias o cosas que necesitan una rápida resolución (agendamientos,
          reprogramaciones, ausencia a sesión, etc): <br />
          <span className="mr-1">📱</span> <strong className="font-medium text-gray-900">WhatsApp:</strong> +1 573 455 4100
        </li>
        <li>
          Para cualquier consulta informativa/explicativa sobre tus procesos, algún bloqueo, las
          asignaciones, tus liquidaciones o cualquier cuestión relevante a tu estadía en Psi
          Mammoliti: <br />
          <span className="mr-1">📧</span> <strong className="font-medium text-gray-900">Mail:</strong> administracion@psimammoliti.com
        </li>
      </ul>
    ),
  },
  {
    resource_key: 'pagos-equidad-sesion',
    stage: 3,
    position: 2,
    title: 'Equidad en la sesión',
    subtitle: 'Qué pasa con el pago según quién no cumplió',
    icon: Scale,
    iconBgClassName: 'bg-violet-100',
    iconColorClassName: 'text-violet-700',
    isNewInStage: false,
    checkbox_label: 'Entendí cómo funciona la equidad de pago entre vos y el paciente.',
    body: (
      <>
        <p className="!mb-3">
          La equidad en la sesión significa que las consecuencias de un incumplimiento — de las
          políticas de cancelación y reprogramación — las asume quien lo generó: vos o el
          paciente, según de quién haya sido la falla.
        </p>
        <p className="!mb-1"><strong className="font-medium text-gray-900">⚖️ La equidad funciona en los dos sentidos:</strong></p>
        <ul className="list-disc ml-4 mb-3 space-y-1.5">
          <li>Si por un error o imprevisto tuyo no podés dar una sesión acordada, esa sesión se reprograma y además se agenda una sesión gratis como compensación al paciente — la asumen entre vos y CX (responsabilidad solidaria).</li>
          <li>Si el paciente no avisa con <HourBadge>6hs</HourBadge> y no se presenta, pierde la sesión sin reembolso.</li>
        </ul>
        <InfoBox>
          Si el error fue de CX u operaciones, se reprograma sin sesión gratuita adicional, pero se pide tu flexibilidad para reubicar el espacio.
        </InfoBox>
      </>
    ),
  },
  {
    resource_key: 'packs',
    stage: 3,
    position: 3,
    title: '📦 Packs de 4 sesiones',
    subtitle: 'Herramienta opcional para mejorar la adherencia',
    icon: Package,
    iconBgClassName: 'bg-blue-100',
    iconColorClassName: 'text-blue-700',
    isNewInStage: true,
    checkbox_label: 'Entendí cómo funcionan los packs y que solo aplican para procesos individuales.',
    body: (
      <>
        <p className="!mb-2">
          Si el paciente abona 4 sesiones por adelantado, recibe un <Badge color="green">20% de descuento</Badge>. El
          descuento lo asume la administración — a vos no te cambia nada en la liquidación.
        </p>
        <p className="!mb-2">
          No es obligatorio mencionarlo, pero es recomendable cuando el paciente muestra disposición a continuar.
          Si está interesado, derivalo a CX.
        </p>
        <Highlight title="📌 Solo para procesos individuales">
          Los packs no aplican a procesos de pareja, familia ni grupales.
        </Highlight>
      </>
    ),
  },
];

export function getJourneyResourcesForStage(stage: number): JourneyResource[] {
  return JOURNEY_RESOURCES.filter((r) => r.stage === stage).sort((a, b) => a.position - b.position);
}
